import { Modal, message } from 'antd'
import FollowedSeriesSlider from 'components/business/Main/Home/FollowedSeriesSlider'
import ShiurimListByDate from 'components/business/Main/Lists/ShiurimListByDate'
import PlayedFilterDropdown from 'components/business/Main/Shiurim/ShiurimFilter/PlayedFilterDropdown'
import Header from 'components/ui/Header'
import { MARK_ALL_FOLLOWED_AS_PLAYED } from 'graphql/mutation/markAllFollowedAsPlayed'
import {
  getShiurimFromFollowed,
  getShiurimFromFollowedVariables,
} from 'graphql/query/__generated__/getShiurimFromFollowed'
import { GET_SHIURIM_FROM_FOLLOWED } from 'graphql/query/getShiurimFromFollowed'
import { useQueryInfo, withQueryInfo } from 'hooks/useQueryInfo'
import { useEffect, useMemo, useState } from 'react'

import { useMutation, useQuery } from '@apollo/client'

import { Wrapper } from '../../styled'

const initialPagination = {
  take: 30,
  page: 1,
  searchParams: {
    dateOrderType: 'DESC',
  },
}

function Following() {
  const { setQueryInfo } = useQueryInfo()

  const [page, setPage] = useState(initialPagination.page)
  const [loadingNext, setLoadingNext] = useState(false)
  const [played, setPlayed] = useState<boolean | undefined>(false) // default Unplayed

  const variables = useMemo(
    () => ({ ...initialPagination, searchParams: { ...initialPagination.searchParams, played } }),
    [played]
  )

  const { data, loading, fetchMore, refetch } = useQuery<getShiurimFromFollowed, getShiurimFromFollowedVariables>(
    GET_SHIURIM_FROM_FOLLOWED,
    { variables, fetchPolicy: 'cache-and-network' }
  )

  const [markAllFollowedAsPlayed] = useMutation<{ markAllFollowedAsPlayed: boolean }>(MARK_ALL_FOLLOWED_AS_PLAYED)

  const MARK_ALL_ENABLED = true

  const onMarkAllAsRead = () => {
    const count = data?.getShiurimFromFollowed?.meta?.total
    Modal.confirm({
      title: 'Mark all as read?',
      content: count
        ? `Mark all ${count} shiurim in your Following feed as read?`
        : 'Mark all shiurim in your Following feed as read?',
      okText: 'Mark all as read',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await markAllFollowedAsPlayed()
          message.success('Following marked as read')
          refetch()
        } catch (e) {
          message.error('Could not mark Following as read')
        }
      },
    })
  }

  useEffect(() => {
    setQueryInfo({ query: GET_SHIURIM_FROM_FOLLOWED, variables })
  }, [setQueryInfo, variables])

  const onFetchMore = async () => {
    setLoadingNext(true)

    const nextVariables = { ...variables, page: page + 1 }

    await fetchMore({
      variables: nextVariables,
      updateQuery(previousQueryResult, { fetchMoreResult }) {
        return {
          ...fetchMoreResult,
          getShiurimFromFollowed: {
            ...previousQueryResult.getShiurimFromFollowed,
            ...fetchMoreResult.getShiurimFromFollowed,
            items: [
              ...previousQueryResult.getShiurimFromFollowed.items,
              ...fetchMoreResult.getShiurimFromFollowed.items,
            ],
          },
        }
      },
    })

    setQueryInfo({ query: GET_SHIURIM_FROM_FOLLOWED, variables: nextVariables })

    setPage((prev) => prev + 1)
    setLoadingNext(false)
  }

  return (
    <>
      <Header isBackButton title="Following" />
      <Wrapper id="scrollable-wrapper">
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 0' }}>
          <PlayedFilterDropdown
            value={played}
            onChange={setPlayed}
            onMarkAllAsRead={MARK_ALL_ENABLED ? onMarkAllAsRead : undefined}
          />
        </div>
        <FollowedSeriesSlider />
        <ShiurimListByDate
          items={data?.getShiurimFromFollowed?.items}
          next={data?.getShiurimFromFollowed?.meta?.next}
          loadingNext={loadingNext}
          onFetchMore={onFetchMore}
          loading={loading}
          isPlayAllTodayAvailable
        />
      </Wrapper>
    </>
  )
}

export default withQueryInfo(Following)
